import * as React from "react"
import { useTranslation } from "react-i18next"
import { useParams } from "react-router-dom"

import Details from "Catalog/Edit/shared/components/Details"
import EditLayout from "Catalog/Edit/shared/components/EditLayout"
import useDelayedRenderedItem from "Catalog/Edit/shared/hooks/useDelayedRenderedItem"
import useCatalogNavigation from "Catalog/shared/hooks/useCatalogNavigation"
import useFindItemOrRedirect from "Catalog/shared/hooks/useFindItemOrRedirect"
import useKeyboardNavigation from "Catalog/shared/hooks/useKeyboardNavigation"
import useEditor from "shared/hooks/useEditor"
import { DiscountId } from "shared/hubrise/ids"
import { IDiscount } from "shared/hubrise/models"
import * as Discount from "shared/hubrise/utils/Discount"

import Content from "./Content"
import Navigator from "./Navigator"
import Toolbar from "./Toolbar"

const Discounts = (): JSX.Element => {
  const { t } = useTranslation()
  const params = useParams<{ discountId: string }>()

  const { catalog } = useEditor()
  const items = catalog.data.discounts

  const { catalogNavigation, visit } = useCatalogNavigation()
  const indexPath = catalogNavigation.editPath("discounts")

  const selectedItem = useFindItemOrRedirect<IDiscount>(
    params.discountId,
    (id) => Discount.byId(catalog, DiscountId(id)),
    indexPath,
  )

  const renderedItem = useDelayedRenderedItem(selectedItem)

  React.useEffect(() => {
    visit("discount", selectedItem)
  }, [selectedItem, visit])

  useKeyboardNavigation<IDiscount>({
    item: selectedItem,
    collection: catalog.data.discounts,
    indexPath: () => indexPath,
    showPath: (discount) => catalogNavigation.editPath("discount", discount.id),
  })

  return (
    <EditLayout
      toolbar={<Toolbar discount={renderedItem} />}
      navigator={items.length > 0 ? <Navigator items={items} selectedId={selectedItem?.id} /> : null}
      details={
        renderedItem ? (
          <Details.Main testId="catalog-details" resourceId={renderedItem.id}>
            <Content discount={renderedItem} />
          </Details.Main>
        ) : null
      }
      none={t("discount.none")}
      noneSelected={t("discount.none_selected")}
      testId="catalog-discounts"
    />
  )
}

export default Discounts
